import React from 'react';
import { Activity, Droplets, Gauge, Waves, Zap } from 'lucide-react';
import { finiteNumber, formatValue } from '../format';

function KpiCard({ reading }) {
  const value = finiteNumber(reading.value);
  const baseline = finiteNumber(reading.baseline);
  const level = String(reading.affected?.level || '').toUpperCase();
  const direction = String(reading.affected?.direction || '').toUpperCase();
  const tone = value === null ? 'grey' : level === 'ALERT' ? 'red' : level === 'WARNING' ? 'amber' : 'green';
  const status = value === null ? 'Missing' : direction && level ? direction.replaceAll('_', ' ') : 'Normal';
  const delta =
    value !== null && baseline !== null && baseline !== 0
      ? ((value - baseline) / Math.abs(baseline)) * 100
      : null;

  const Icon = reading.Icon;

  return (
    <article className={`kpi-card tone-${tone}`}>
      <div className="kpi-card-top">
        <span className="kpi-icon" aria-hidden="true">
          <Icon size={19} />
        </span>
        <span className={`status-pill is-${tone}`}>{status}</span>
      </div>
      <div className="kpi-copy">
        <span>{reading.label}</span>
        <strong className={value === null ? 'is-missing' : ''}>
          {formatValue(value, reading.unit)}
        </strong>
      </div>
      <div className="kpi-delta">
        {delta === null ? (
          <span>Baseline unavailable</span>
        ) : (
          <>
            <b className={delta < 0 ? 'delta-down' : delta > 0 ? 'delta-up' : ''}>
              {delta === 0 ? '0%' : `${delta < 0 ? '↓' : '↑'} ${Math.abs(delta).toFixed(0)}%`}
            </b>
            <span>vs. baseline</span>
          </>
        )}
      </div>
    </article>
  );
}

export function Readings({ payload }) {
  if (!payload) return null;
  const values = payload.current_values || {};
  const ranges = payload.baseline_ranges || {};
  const affected = new Map((payload.affected_readings || []).map((item) => [item.metric, item]));

  const readings = [
    {
      key: 'current',
      label: 'Avg Current',
      value: values.current?.avg,
      unit: 'A',
      Icon: Activity,
      affected: affected.get('current'),
      baseline: ranges.current?.avg?.median,
    },
    {
      key: 'voltage',
      label: 'Avg Voltage',
      value: values.voltage?.avg,
      unit: 'V',
      Icon: Zap,
      affected: affected.get('voltage'),
      baseline: ranges.voltage?.avg?.median,
    },
    {
      key: 'pf',
      label: 'Avg Power Factor',
      value: values.pf?.avg,
      unit: '',
      Icon: Gauge,
      affected: affected.get('pf'),
      baseline: ranges.pf?.avg?.median,
    },
    {
      key: 'power',
      label: 'Power',
      value: values.power?.total_kw,
      unit: 'kW',
      Icon: Waves,
      affected: affected.get('power'),
      baseline: ranges.power?.total_kw?.median,
    },
    {
      key: 'flow',
      label: 'Flow',
      value: values.flow?.flowrate,
      unit: 'm³/h',
      Icon: Droplets,
      affected: affected.get('flow'),
      baseline: ranges.flow?.flowrate?.median,
    },
  ];

  return (
    <section className="kpi-grid" aria-label="Live telemetry summary">
      {readings.map((item) => (
        <KpiCard key={item.key} reading={item} />
      ))}
    </section>
  );
}

export default Readings;
